import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { BFData } from '@cf-shared/data-model/user-state';
import { BFMethod, Gender } from '@cf-shared/enums';
import { roundToPrecision } from '@cf-shared/shared-util';
import { CsSliderTickmark } from 'components/cs-slider/cs-slider';
import { skinfoldSiteName } from 'util/formatting';
import { ZoomImagePopover } from 'components/zoom-image-popover/zoom-image-popover';
import { PopoverController } from 'providers/popover-controller';
import { DestroyGuard } from 'services/browser-utils';

import { LL } from 'i18n/i18n.service';
@Component({
  selector: 'bodyfat-input',
  templateUrl: 'bodyfat-input.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BodyfatInputComponent{
  protected LL = LL;
  private destroyGuard = new DestroyGuard();

  constructor(
    private popoverCtrl: PopoverController,
    private elementRef: ElementRef,
    private cd: ChangeDetectorRef
  ) {}

  skinfolds = [
    { id: 'chest_mm', name: skinfoldSiteName('chest'), male: true, photoM: 'assets/imgs/Skinfold-M-Chest.svg' },
    {
      id: 'tricep_mm',
      name: skinfoldSiteName('tricep'),
      female: true,
      male: true,
      photoM: 'assets/imgs/Skinfold-M-Triceps.svg',
      photoF: 'assets/imgs/Skinfold-F-Triceps.svg',
    },
    { id: 'suprailiac_mm', name: skinfoldSiteName('suprailiac'), female: true, photoF: 'assets/imgs/Skinfold-F-Suprailiac.svg' },
    {
      id: 'umbilical_mm',
      name: skinfoldSiteName('umbilical'),
      female: true,
      male: true,
      photoM: 'assets/imgs/Skinfold-M-Umbilical.svg',
      photoF: 'assets/imgs/Skinfold-F-Umbilical.svg',
    },
    {
      id: 'thigh_mm',
      name: skinfoldSiteName('thigh'),
      female: true,
      male: true,
      photoM: 'assets/imgs/Skinfold-M-Thigh.svg',
      photoF: 'assets/imgs/Skinfold-F-Thigh.svg',
    },
  ];

  bfImageForPercentMale = [
    {
      value: 5,
      image: 'assets/imgs/male-5.svg',
    },
    {
      value: 10,
      image: 'assets/imgs/male-10.svg',
    },
    {
      value: 15,
      image: 'assets/imgs/male-15.svg',
    },
    {
      value: 20,
      image: 'assets/imgs/male-20.svg',
    },
    {
      value: 30,
      image: 'assets/imgs/male-30.svg',
    },
    {
      value: 40,
      image: 'assets/imgs/male-40.svg',
    },
  ]

  bfImageForPercentFemale = [
    {
      value: 10,
      image: 'assets/imgs/female-10.svg',
    },
    {
      value: 15,
      image: 'assets/imgs/female-15.svg',
    },
    {
      value: 20,
      image: 'assets/imgs/female-20.svg',
    },
    {
      value: 25,
      image: 'assets/imgs/female-25.svg',
    },
    {
      value: 30,
      image: 'assets/imgs/female-30.svg',
    },
    {
      value: 35,
      image: 'assets/imgs/female-35.svg',
    },
    {
      value: 40,
      image: 'assets/imgs/female-40.svg',
    },
    {
      value: 50,
      image: 'assets/imgs/female-50.svg',
    }
  ]

   bfSliderTickmarksMale: CsSliderTickmark[] = [
      { value: 5, label: '5%' },
      { value: 10, label: '10%' },
      { value: 15, label: '15%' },
      { value: 20, label: '20%' },
      { value: 30, label: '30%' },
      { value: 40, label: '40%' },
    ]
    bfSliderTickmarksFemale: CsSliderTickmark[] = [
      { value: 10, label: '10%' },
      { value: 15, label: '15%' },
      { value: 20, label: '20%' },
      { value: 25, label: '25%' },
      { value: 30, label: '30%' },
      { value: 35, label: '35%' },
      { value: 40, label: '40%' },
      { value: 50, label: '50%' },
    ]

  @Output() methodChange: EventEmitter<BFMethod> = new EventEmitter<BFMethod>();
  @Input() shouldOfferTapeMeasureMethod: boolean = true;
  @Input() bfData: BFData;
  @Output() bfDataChange: EventEmitter<BFData> = new EventEmitter<BFData>();
  @Input() gender: Gender = Gender.male;

  @ViewChild('bfImagesHolder') bfImagesHolder: ElementRef;

  BFMethod = BFMethod;
  Gender = Gender;
  roundToPrecision = roundToPrecision;

  ngOnChanges(changes: SimpleChanges) {
    if (changes.gender) {
      this.bfImageForPercentCurrent = this.gender === Gender.male ? this.bfImageForPercentMale : this.bfImageForPercentFemale;
    }
    // Trigger change detection when bfData is initially set or changed
    if (changes.bfData) {
      this.cd.markForCheck();
      // Handle initial method setup
      if (this.bfData && this.bfData.method === BFMethod.Self) {
        // Use safeTimeout to ensure view is ready before updating position
        this.destroyGuard.safeTimeout(() => this.updatePositionForBF(), 0);
      }
    }
  }

  prevMethod: BFMethod = undefined;
  prevSelfBf_pc: number = undefined;
  prevCaliperMeasures: { [key: string]: number[] } = {};
  prevWaistMeasures: { [key: string]: number } = {};
  ngDoCheck() {
    if (this.bfData && this.prevMethod !== this.bfData.method) {
      this.prevMethod = this.bfData.method;
      if (this.bfData.method === BFMethod.Self) {
        this.updatePositionForBF();
      }
      this.cd.markForCheck();
    }
    if (this.bfData && this.bfData.method === BFMethod.Self && this.prevSelfBf_pc !== this.bfData.bfSelf_pc) {
      this.prevSelfBf_pc = this.bfData.bfSelf_pc;
      this.updatePositionForBF();
    }
    if (this.bfData && this.bfData.method === BFMethod.Caliper) {
      for (let skinfold of this.skinfolds) {
        if (this.prevCaliperMeasures[skinfold.id] === undefined) {
          this.prevCaliperMeasures[skinfold.id] = [];
          for (let i of [0, 1, 2, 3, 4]) {
            this.prevCaliperMeasures[skinfold.id][i] = this.bfData[skinfold.id][i];
          }
          this.cd.markForCheck();
          continue;
        }
        for (let i of [0, 1, 2, 3, 4]) {
          if (!this.bfData[skinfold.id]) {
            this.bfData[skinfold.id] = [];
          }
          if (this.prevCaliperMeasures[skinfold.id][i] !== this.bfData[skinfold.id][i]) {
            this.prevCaliperMeasures[skinfold.id][i] = this.bfData[skinfold.id][i];
            this.cd.markForCheck();
          }
        }
      }
    }
    if (this.bfData && this.bfData.method === BFMethod.Tape) {
      if (this.prevWaistMeasures['waist_cm'] !== this.bfData.waist_cm) {
        this.prevWaistMeasures['waist_cm'] = this.bfData.waist_cm;
        this.cd.markForCheck();
      }
      if (this.prevWaistMeasures['hip_cm'] !== this.bfData.hip_cm) {
        this.prevWaistMeasures['hip_cm'] = this.bfData.hip_cm;
        this.cd.markForCheck();
      }
      if (this.prevWaistMeasures['neck_cm'] !== this.bfData.neck_cm) {
        this.prevWaistMeasures['neck_cm'] = this.bfData.neck_cm;
        this.cd.markForCheck();
      }
      if (this.prevWaistMeasures['abdomen_cm'] !== this.bfData.abdomen_cm) {
        this.prevWaistMeasures['abdomen_cm'] = this.bfData.abdomen_cm;
        this.cd.markForCheck();
      }
    }
  }

  ngOnInit() {
    // Initialize bfImageForPercentCurrent based on gender
    this.bfImageForPercentCurrent = this.gender === Gender.male ? this.bfImageForPercentMale : this.bfImageForPercentFemale;
  }

  ngAfterViewInit() {
    this.resizeObserver.observe(this.elementRef.nativeElement);
    this.onResize();
    // Ensure initial position update for Self method
    if (this.bfData && this.bfData.method === BFMethod.Self) {
      this.destroyGuard.safeTimeout(() => {
        this.updatePositionForBF();
        this.cd.markForCheck();
      }, 0);
    }
  }

  ngOnDestroy() {
    this.destroyGuard.destroy();
    this.resizeObserver.disconnect();
  }
  
  onMethodChange() {
    this.methodChange.emit(this.bfData.method);
    if (this.bfData.method === BFMethod.Self) {
      this.updatePositionForBF();
    }
  }

  ignoreNextBgImageHolderScrollScroll = true;
  ignoreNextUpdatePositionForBF = true;
  bfImageForPercentCurrent = this.bfImageForPercentMale;

  resizeObserver: ResizeObserver = new ResizeObserver(() => { this.onResize(); });

  onResize() {
    let holderElem = this.bfImagesHolder && this.bfImagesHolder.nativeElement as HTMLElement;
    let sliderElem = holderElem && holderElem.querySelector('div.images-slider') as HTMLElement;
    let imageElem = sliderElem && sliderElem.querySelector('div.image') as HTMLElement;
    if (sliderElem && holderElem && imageElem) {
      const padding = holderElem.clientWidth * 0.5 - imageElem.clientWidth * 0.5;
      sliderElem.style.setProperty('padding-right', `${padding}px`);
      sliderElem.style.setProperty('padding-left', `${padding}px`);
    }
    this.updatePositionForBF();
  }

  onBfImageHolderScroll(ev: Event) {
    if (this.ignoreNextBgImageHolderScrollScroll) {
      this.ignoreNextBgImageHolderScrollScroll = false;
      return;
    }
    void ev;
    let holderElem = this.bfImagesHolder && this.bfImagesHolder.nativeElement as HTMLElement;
    let sliderElem = holderElem && holderElem.querySelector('div.images-slider') as HTMLElement;

    const maxScrollLeft = sliderElem.scrollWidth - sliderElem.clientWidth;
    const scrollLeft = sliderElem.scrollLeft;
    const ratioTotal = scrollLeft / maxScrollLeft;
    if (this.bfData.method === BFMethod.Self) {
      this.ignoreNextUpdatePositionForBF = true;
      let numImages = this.bfImageForPercentCurrent.length;
      for (let iImageLeft = 0; iImageLeft < numImages - 1; iImageLeft++) {
        const ratioBetween = ratioTotal * (numImages - 1) - iImageLeft;
        if (ratioBetween >= 0 && ratioBetween < 1) {
          let bf_pc = this.bfImageForPercentCurrent[iImageLeft].value + ratioBetween * (this.bfImageForPercentCurrent[iImageLeft + 1].value - this.bfImageForPercentCurrent[iImageLeft].value);
          this.bfData.bfSelf_pc = roundToPrecision(bf_pc, 0.1);
          break;
        }
      }
    }
  }

  onBfSliderValueChanged(value: number) {
    if (this.bfData.method === BFMethod.Self) {
      this.prevSelfBf_pc = value;
    }
    this.updatePositionForBF();

    this.bodyfatPercentChanged();
  }

  // update position offset for bodyfat images after the slider has been moved
  updatePositionForBF() {
    if (this.ignoreNextUpdatePositionForBF) {
      this.ignoreNextUpdatePositionForBF = false;
      return;
    }

    if (this.bfData.method !== BFMethod.Self) {
      return;
    }

    let bf_pc = this.bfData.bfSelf_pc;

    let holderElem = this.bfImagesHolder && this.bfImagesHolder.nativeElement as HTMLElement;
    let sliderElem = holderElem && holderElem.querySelector('div.images-slider') as HTMLElement;
    let imageElem = sliderElem && sliderElem.querySelector('div.image') as HTMLElement;
    if (!holderElem || !sliderElem || !imageElem) {
      return;
    }

    // let holderWidth = holderElem.clientWidth;
    // let sliderWidth = sliderElem.clientWidth;
    // let imageWidth = imageElem.clientWidth;
    // there are N images
    let numImages = this.bfImageForPercentCurrent.length;
    // we need to find in between which from 0..N-1 the current percentage is, and at which interpolation factor between that one and the next
    let iImageLeft = numImages - 1; // assume the last one
    let ratioBetween = 0;
    for (let i = 0; i < numImages; i++) {
      if (bf_pc < this.bfImageForPercentCurrent[i].value) {
        if (i === 0) {
          iImageLeft = 0;
          ratioBetween = 0;
        } else {
          iImageLeft = i - 1;
          ratioBetween = (bf_pc - this.bfImageForPercentCurrent[iImageLeft].value) / (this.bfImageForPercentCurrent[iImageLeft + 1].value - this.bfImageForPercentCurrent[iImageLeft].value);
          break;
        }
      }
    }

    // where are we between images (0 represents exactly at the first one, 1 represents exactly at the last one)?
    let ratioTotal = (iImageLeft + ratioBetween) / (numImages - 1);
    /* We need to calculate the pixel offset to move the slider so that the center of the holder (where we have the arrow)
    will match the center of the image that corresponds to the current bodyfat percentage. */
    const maxScrollLeft = sliderElem.scrollWidth - sliderElem.clientWidth;
    const scrollLeft = maxScrollLeft * ratioTotal;
    this.ignoreNextBgImageHolderScrollScroll = true;
    sliderElem.scrollTo({
      left: scrollLeft,
      behavior: 'instant',
    })
  }

  @ViewChild('zoomImagePopover') zoomImagePopover: TemplateRef<any>;
  zoomImage: string;

  async handleImageZoomButtonClick(ev: Event, imageSrc: string) {
    ev.stopPropagation();
    ev.stopImmediatePropagation();
    ev.preventDefault();

    this.zoomImage = imageSrc;

    let targetElement = ev.currentTarget as HTMLElement;

    await this.popoverCtrl.present(ZoomImagePopover, { imageSrc: this.zoomImage }, {
      targetFromElement: targetElement,
      targetToElement: document.body,
      animate: true,
    });
  }

  bodyfatPercentChanged() {
    this.bfDataChange.emit(this.bfData);
  }
}